import {
  Component,
  OnInit,
  OnDestroy,
  ChangeDetectionStrategy,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { RouterModule } from '@angular/router';
import { Subject } from 'rxjs';
import { takeUntil, switchMap, finalize } from 'rxjs/operators';
import { CartService } from '@app/services/cart.service';
import { UiService } from '@app/services/ui.service';
import {
  CartValidationService,
  ValidatedProduct,
} from '@app/services/cart-validation.service';
import { GalaxyButtonDirective } from '@app/directives/galaxy-button.directive';

interface EnrichedCartItem extends ValidatedProduct {
  quantity: number;
}

@Component({
  selector: 'app-mini-cart',
  imports: [CommonModule, RouterModule, GalaxyButtonDirective],
  templateUrl: './mini-cart.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrls: ['./mini-cart.component.css'],
})
export class MiniCartComponent implements OnInit, OnDestroy {
  private destroy$ = new Subject<void>();
  enrichedCartItems: EnrichedCartItem[] = [];
  subtotal = 0;
  isLoading = true;

  constructor(
    private cartService: CartService,
    private cartValidationService: CartValidationService,
    public uiService: UiService,
  ) {}

  ngOnInit(): void {
    this.cartService.quantity$
      .pipe(
        takeUntil(this.destroy$),
        switchMap(() => {
          this.isLoading = true;
          const cartState = this.cartService.getCartState();
          if (cartState.length === 0) {
            return [];
          }
          return this.cartValidationService.validateCartItems(cartState);
        }),
        finalize(() => (this.isLoading = false)),
      )
      .subscribe((validatedProducts) => {
        const cartMap = this.cartService.getCartItemsMap();

        this.enrichedCartItems = validatedProducts.map((product) => {
          const key = `${product.productId}_${product.formatKey}`;
          const entry = cartMap.get(key);
          return {
            ...product,
            quantity: entry ? entry.quantity : 0,
          };
        });

        this.calculateSubtotal();
        this.isLoading = false;
      });
  }

  calculateSubtotal(): void {
    this.subtotal = this.enrichedCartItems.reduce((acc, item) => {
      return acc + (item.price || 0) * item.quantity;
    }, 0);
  }

  close(): void {
    this.uiService.closeMiniCart();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
